import { Component, ChangeDetectionStrategy, inject, input, output, viewChildren, linkedSignal, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { Platform, IonItem } from '@ionic/angular';
import { Pass, Username } from '@common/constants';

import { Events } from '@providers/common/events.service';
import { DataService } from '@providers/common/data.service';
import { CapacitorUtilsService } from '@providers/common/capacitorUtils.service';
import { AppStorageProvider } from '@providers/common/app-storage.service';
import { NgStyle, NgClass } from '@angular/common';

/**
 * @name MyMenuComponent
 * @description Componente para mostrar el menu de CIVAR en el menu lateral
 */
@Component({
    selector: 'my-menu',
    templateUrl: './my-menu.component.html',
    styleUrls: ['./my-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgStyle,
        IonItem,
        NgClass,
    ],
})
export class MyMenuComponent {
    private events = inject(Events);
    private readonly destroyRef = inject(DestroyRef);
    private capacitorService = inject(CapacitorUtilsService);
    private plt = inject(Platform);
    private router = inject(Router);
    private dataService = inject(DataService);
    private storage = inject(AppStorageProvider);

    public readonly level = input(0);
    public readonly menu = input('');
    readonly items = input<any[]>([]);
    // Submenus abiertos/cerrados; se reinicia (todos cerrados) cuando cambia items.
    // Se modifica en sitio (showSubMenu()[i] = ...), igual que antes
    public readonly showSubMenu = linkedSignal(() => Array((this.items() ?? []).length).fill(false));
    public readonly submenuOpen = output<any>();
    readonly submenuElems = viewChildren<any>('submenuElem');

    constructor() {
        this.events.subscribe('menues:close', (level) => {
            if (this.level() >= level) {
                for (const i in this.showSubMenu()) {
                    this.showSubMenu()[i] = false;
                }
                this.submenuElems().forEach(
                    (item) => (item.nativeElement.style.maxHeight = '0px'),
                );
            }
        }, this.destroyRef);
    }

    /**
     * @name onClick
     * @desc Metodo que que se usa para la accion de un tab del menu
     */
    onClick(item: any, idx: number) {
        if (item.OpcionMenu == null) {
            this.openPage(item);
        } else {
            this.events.publish('menues:close', this.level() + 1);
            for (const i in this.showSubMenu()) {
                if (parseInt(i) != idx) this.showSubMenu()[i] = false;
            }
            this.submenuElems().forEach((item, i) => {
                if (i != idx) item.nativeElement.style.maxHeight = '0px';
            });
            this.showSubMenu()[idx] = !this.showSubMenu()[idx];
            const elem = this.submenuElems().find((item, i) => i === idx);
            if (elem && elem.nativeElement) {
                const height = this.showSubMenu()[idx]
                    ? elem.nativeElement.scrollHeight
                    : 0;
                this.submenuOpen.emit(height);
                elem.nativeElement.style.maxHeight = height + 'px';
            }
        }
    }

    resizeContainer(ev: any) {
        for (const item of this.showSubMenu()) {
            if (item) {
                const elem = this.submenuElems().find(
                    (el, i) => i === this.showSubMenu().indexOf(item),
                );
                if (elem && elem.nativeElement) {
                    this.submenuOpen.emit(elem.nativeElement.scrollHeight + ev);
                    elem.nativeElement.style.maxHeight =
                        elem.nativeElement.scrollHeight + ev + 'px';
                }
                break;
            }
        }
    }

    /**
     * @name openPage
     * @description Metodo que abre una pagina del menu
     */
    openPage(item: any) {
        // console.log('openPage, item: ', JSON.stringify(item));

        if (item.Desc_Pagina) {
            if (item.Desc_Pagina.includes('/ARTUS')) {
                let dashboard: number;
                switch (this.menu()) {
                    case 'ADMINISTRACION':
                    case 'ADMINISTRATION':
                        dashboard = 29;
                        break;
                    case 'OPERACION':
                    case 'OPERATION':
                        dashboard = 126;
                        break;
                    case 'CALIDAD':
                    case 'QA':
                        dashboard = 44;
                        break;
                    case 'RH':
                    case 'RRHH':
                        dashboard = 95;
                        break;
                    default:
                        dashboard = 0;
                        break;
                }
                this.autoLoginARTUS(item, Username, Pass, dashboard);
            } else {
                if (
                    this.plt.is('mobile') &&
                    (this.plt.is('android') || this.plt.is('ios'))
                ) {
                    // console.log('IN APP BROWSER CREATE');
                    this.capacitorService.goToLaunchBrowser(item.Desc_Pagina);
                } else {
                    if (item.Bit_Frame) {
                        // console.log('FRAME OPEN');
                        const routeParams = {
                            // Se genera un objeto con los parametros a transmitir a la siguiente route
                            externalURL: item.Desc_Pagina, // URL del item a mostrar en el frame
                            routeId: item.Id_Cnsc_Menu,
                        };
                        this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
                        this.storage.put(routeParams.routeId, routeParams); // Con ayuda del provider StorageService asignamos el objeto parametros a un iD, para de esta manera poder obtenerlo desde el storage en caso de necesitarlo en la siguiente ruta/pantalla

                        this.router.navigate(['/quantum/civar/' + routeParams.routeId], {
                            replaceUrl: true,
                        });
                    } else {
                        // console.log('WINDOW OPEN');

                        window.open(item.Desc_Pagina, '_blank');
                    }
                }
            }
        }
    }

    /**
     * @name autoLoginARTUS
     * @description Metodo que hace un inicio de sesion en el sistem ARTUS
     */
    autoLoginARTUS(item, user, pass, dashboard) {
        if (user != null && pass != null) {
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://aws.csigroup.com.mx/ARTUSG9/LoadStage.php';
            form.target = '_blank';

            form.method = 'POST';
            const element1 = document.createElement('input');
            const element2 = document.createElement('input');
            const element3 = document.createElement('input');
            const element4 = document.createElement('input');
            const element5 = document.createElement('input');

            element1.id = 'repository';
            element1.name = 'txtRepositorio';
            element1.value = 'BISunusProd';
            form.appendChild(element1);

            element2.id = 'username';
            element2.name = 'txtUser';
            element2.value = user;
            form.appendChild(element2);

            element3.id = 'password';
            element3.name = 'txtPassword';
            element3.value = pass;
            form.appendChild(element3);

            element4.name = 'txtNumEsc';
            element4.value = dashboard;
            form.appendChild(element4);

            element5.name = 'txtParametros';
            element5.value = 'TPParams';
            form.appendChild(element5);

            document.body.appendChild(form);

            form.submit();
        } else {
            if (
                this.plt.is('mobile') &&
                (this.plt.is('android') || this.plt.is('ios'))
            ) {
                // console.log('IN APP BROWSER CREATE');
                this.capacitorService.goToLaunchBrowser(item.Desc_Pagina);
            } else {
                if (item.Bit_Frame) {
                    // console.log('FRAME OPEN');

                    const routeParams = {
                        // Se genera un objeto con los parametros a transmitir a la siguiente route
                        externalURL: item.Desc_Pagina, // URL del item a mostrar en el frame
                        routeId: item.Id_Cnsc_Menu,
                    };
                    this.dataService.setData(routeParams.routeId, routeParams); // Con ayuda del provider DataService asignamos el objeto parametros a un iD, para de esta manera poder recibirlo en la siguiente ruta/pantalla
                    this.storage.put(routeParams.routeId, routeParams); // Con ayuda del provider StorageService asignamos el objeto parametros a un iD, para de esta manera poder obtenerlo desde el storage en caso de necesitarlo en la siguiente ruta/pantalla

                    this.router.navigate(['/quantum/civar/' + routeParams.routeId], {
                        replaceUrl: true,
                    });
                } else {
                    // console.log('WINDOW OPEN');
                    window.open(item.Desc_Pagina, '_blank');
                }
            }
        }
    }
}
